import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { useAuiState } from '@assistant-ui/react';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { SkillInputFlow, SkillInputFlowReceipt } from './SkillInputFlow';
import { findPriorSkillUrlFromMessages } from './priorSkillUrl';
import {
  isConfigureSkillToolArgs,
  isSkillConfigureAnswersPayload,
  summarizeSkillAnswers,
  type ConfigureSkillToolArgs,
  type SkillConfigureAnswersPayload
} from './skillFlow.types';

function parseArgs(args: unknown): ConfigureSkillToolArgs | null {
  if (!isConfigureSkillToolArgs(args)) {
    return null;
  }
  return args;
}

function parseAnswersResult(
  result: unknown
): SkillConfigureAnswersPayload | null {
  if (isSkillConfigureAnswersPayload(result)) {
    return result;
  }
  if (typeof result !== 'string' || !result.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(result);
    return isSkillConfigureAnswersPayload(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Shared Tool UI for all chat skill configure_* human tools. */
export const ConfigureSkillToolUI: ToolCallMessagePartComponent = ({
  args,
  result,
  addResult
}) => {
  const { data: userInfo } = useGetUserInfoQuery();
  const domains = Array.isArray(userInfo?.data?.domains)
    ? userInfo.data.domains
    : [];
  const priorUrl = useAuiState(s =>
    findPriorSkillUrlFromMessages(s.thread.messages)
  );
  const parsed = parseArgs(args);

  if (!parsed) {
    return (
      <div className="text-destructive text-xs">
        پیکربندی مهارت نامعتبر است.
      </div>
    );
  }

  const answers = parseAnswersResult(result);
  if (answers) {
    return (
      <SkillInputFlowReceipt
        id={parsed.id}
        title={parsed.title}
        summary={summarizeSkillAnswers(parsed.steps, answers.answers, {
          domains
        })}
      />
    );
  }

  if (result != null) {
    return (
      <SkillInputFlowReceipt
        id={parsed.id}
        title={parsed.title}
        summary={[{ label: 'وضعیت', value: 'در حال پردازش…' }]}
      />
    );
  }

  return (
    <SkillInputFlow
      id={parsed.id}
      title={parsed.title}
      description={parsed.description}
      steps={parsed.steps}
      priorUrl={priorUrl}
      onComplete={(answersPayload) => {
        addResult({
          phase: 'answers',
          skillCommand: parsed.skillCommand,
          skillExecutionId: parsed.skillExecutionId,
          answers: answersPayload
        });
      }}
    />
  );
};

/** @deprecated Use ConfigureSkillToolUI */
export const ConfigureSeoContentToolUI = ConfigureSkillToolUI;
